import { useEffect, ReactNode, useRef } from 'react';

type Props = {
  children?: ReactNode;
};

/**
 * Wrapper that ensures all iframes have referrerPolicy set to 'strict-origin-when-cross-origin'.
 * For existing iframes with src already loaded, recreates them to properly apply the policy.
 */
export const EntityDatadogReferrerWrapper = ({ children }: Props) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const processed = new WeakSet<HTMLIFrameElement>();

    const fixIframe = (iframe: HTMLIFrameElement) => {
      if (processed.has(iframe)) return;

      processed.add(iframe);

      const src = iframe.getAttribute('src');

      iframe.referrerPolicy = 'strict-origin-when-cross-origin';

      if (src) {
        iframe.src = iframe.src;
      }
    };

    const processIframes = (element: Element) => {
      element.querySelectorAll('iframe').forEach(fixIframe);
    };

    const observer = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        mutation.addedNodes.forEach(node => {
          if (node instanceof HTMLIFrameElement) {
            fixIframe(node);
          } else if (node instanceof Element) {
            processIframes(node);
          }
        });
      });
    });

    processIframes(container);

    observer.observe(container, {
      childList: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  return <div ref={containerRef}>{children}</div>;
};

export default EntityDatadogReferrerWrapper;
